import React, { useState, useEffect } from 'react';
import type { InventoryItem } from '../../../types';
import XCircleIcon from '../../icons/XCircleIcon';

interface CreateInventoryItemDialogProps {
  itemName: string;
  receivedQuantity: number;
  unit: string;
  supplierName?: string;
  onCreate: (item: Omit<InventoryItem, 'id' | 'lastUpdated'>) => void;
  onSkip: () => void;
  onClose: () => void;
}

const units: InventoryItem['unit'][] = ['kg', 'g', 'L', 'ml', 'units'];

const CreateInventoryItemDialog: React.FC<CreateInventoryItemDialogProps> = ({
  itemName,
  receivedQuantity,
  unit,
  supplierName,
  onCreate,
  onSkip,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    name: itemName,
    stock: receivedQuantity,
    unit: (unit && units.includes(unit as any)) ? unit : 'units' as InventoryItem['unit'],
    lowStockThreshold: 10,
    category: '',
    supplier: supplierName || '',
  });

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      name: itemName,
      stock: receivedQuantity,
      unit: (unit && units.includes(unit as any)) ? unit : 'units' as InventoryItem['unit'],
      supplier: supplierName || prev.supplier,
    }));
  }, [itemName, receivedQuantity, unit, supplierName]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'lowStockThreshold' || name === 'stock'
        ? (value === '' ? '' : parseFloat(value) || 0)
        : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || formData.stock < 0) {
      alert('Please fill in all required fields');
      return;
    }

    const newItem: Omit<InventoryItem, 'id' | 'lastUpdated'> = {
      name: formData.name,
      stock: formData.stock,
      unit: formData.unit,
      lowStockThreshold: formData.lowStockThreshold,
      category: formData.category || undefined,
      supplier: formData.supplier || undefined,
    };

    onCreate(newItem);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Create New Inventory Item</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>{itemName}</strong> not found in inventory.
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Received: {receivedQuantity} {unit}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Item Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="stock" className="block text-sm font-medium text-gray-700 mb-1">
                  Stock <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="stock"
                  name="stock"
                  value={formData.stock === 0 ? '' : formData.stock}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">
                  Unit <span className="text-red-500">*</span>
                </label>
                <select
                  id="unit"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  required
                >
                  {units.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="lowStockThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                Low Stock Threshold
              </label>
              <input
                type="number"
                id="lowStockThreshold"
                name="lowStockThreshold"
                value={formData.lowStockThreshold === 0 ? '' : formData.lowStockThreshold}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <input
                type="text"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., Meat, Vegetables"
              />
            </div>

            <div>
              <label htmlFor="supplier" className="block text-sm font-medium text-gray-700 mb-1">
                Supplier
              </label>
              <input
                type="text"
                id="supplier"
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Supplier name"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => {
                onSkip();
                onClose();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Skip
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
            >
              Create Item
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInventoryItemDialog;
